// AI provider transport that routes every AxonProviderKit request through the
// Functions proxy (functions/src/aiProxy.ts). The browser never holds provider
// API keys: StreamingClient is configured with a placeholder key, the real
// credential is injected server-side, and the proxy pipes the SSE bytes back
// so ProviderKit's per-provider parsers keep working unchanged.

import type { FetchTransport } from "axon-provider-kit/client";
import { apiBaseUrl } from "@/firebase/app";
import { currentIdToken } from "@/services/functionsClient";

/** Placeholder passed to ProviderKit as `apiKey`; stripped by the proxy. */
export const PROXY_API_KEY_PLACEHOLDER = "proxy";

/** Provider auth headers that must not leak the placeholder to the proxy. */
const PROVIDER_AUTH_HEADERS = new Set(["authorization", "x-api-key", "x-goog-api-key"]);

const AI_USAGE_LIMIT_MESSAGE = /^(?:This league's|Your) AI token limit for the (?:hour|day|week|month|year) has been reached\. It resets on .+ UTC\.$/;

/**
 * Axon retries 429 provider errors. The SBot proxy also uses 429 for a
 * deliberate token-limit block, which must not be retried. Convert only that
 * known response to a provider-shaped 400 so Axon preserves the human message
 * while classifying it as non-retryable. Ordinary provider/rate-limit 429s are
 * returned untouched.
 */
export async function normalizeAIUsageLimitResponse(response: Response): Promise<Response> {
  if (response.status !== 429) return response;

  let payload: unknown;
  try {
    payload = await response.clone().json();
  } catch {
    return response;
  }

  if (!isRecord(payload) || typeof payload.error !== "string") return response;
  const message = payload.error;
  if (message !== "AI token limit reached" && !AI_USAGE_LIMIT_MESSAGE.test(message)) return response;

  return new Response(JSON.stringify({
    error: {
      message,
      type: "sbot_ai_usage_limit",
      code: "sbot_ai_usage_limit",
    },
  }), {
    status: 400,
    headers: { "Content-Type": "application/json" },
  });
}

export const proxyTransport: FetchTransport = async (request) => {
  const token = await currentIdToken();

  const upstream = new URL(request.url);
  // Gemini carries its key as a query parameter; drop the placeholder.
  upstream.searchParams.delete("key");

  const headers: Record<string, string> = {};
  for (const [name, value] of Object.entries(request.headers)) {
    if (!PROVIDER_AUTH_HEADERS.has(name.toLowerCase())) {
      headers[name] = value;
    }
  }
  headers["Authorization"] = `Bearer ${token}`;
  headers["x-sbot-upstream-url"] = upstream.toString();

  const response = await fetch(`${apiBaseUrl}/ai/stream`, {
    method: "POST",
    headers,
    body: (request.body ?? null) as BodyInit | null,
    signal: request.signal ?? null,
  });
  return normalizeAIUsageLimitResponse(response);
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
